var HashMap = require("./hashMap.js");
var HashSet = require("./hashSet.js");
var Heap = require("./heap.js");
var Vector = require("./vector.js");
var Bins = require("./bins.js");

// TODO: Separate Directed / Undirected graphs, graphs that allow multiple edges between same vertices and graphs that allow loops. Make sure all applicable algorithms works for the different graph types
var Graph = function (vhash) {  // This graph is a directed graph that does not allow multiple edges nor loops.
    this._vhash = vhash;
    this._vertices = new HashMap(this._vhash);
    this._edges = new HashSet();
};

var Vertex = function (data, cost) {
    this.data = data;
    this.cost = (cost == undefined) ? Vector.ZERO : cost instanceof Vector ? cost : new Vector(cost); // TODO: make this more general by passing general costClass in graph constructor which should have compare, add, sub, zero defined

    this.edges = new HashSet();
    this.fanin = new HashSet();
    this.fanout = new HashSet();
};

var Edge = function (source, target, cost, data) {
    this.source  = source;
    this.target  = target;
    this.cost = (cost == undefined) ? Vector.ZERO : cost instanceof Vector ? cost : new Vector(cost);
    this.data = data;
};

// VERTEX PROTOTYPE
Vertex.prototype.getConnectedVertices = function () {
    var self = this;
    return this.edges.map(function(edge) {
       return edge.other(self);
    });
};

Vertex.prototype.getNextVertices = function () {
    var self = this;
    return this.fanout.map(function(edge) {
       return edge.other(self);
    });
};

// EDGE PROTOTYPE
Edge.prototype.other = function(vertex) {
    if (this.source === vertex) return this.target;
    else return this.source;
};

Edge.prototype.hasVertex = function(vertex) {
    if (this.source === vertex) return true;
    if (this.target === vertex) return true;
    return false;
};

// TODO: better name for this?
Edge.prototype.toObject = function() {
    return {
        source: this.source.data,
        target: this.target.data,
        cost: this.cost.dimension() === 1 ? this.cost.elements[0] : this.cost.elements,
        data: this.data
    };
};

// GRAPH PROTOTYPE
// Deal with vertices:
Graph.prototype.addVertex = function (data, cost) {
    var vertex = this._vertices.get(data);
    if(!vertex) {
        this._vertices.put(data, new Vertex(data, cost));
    } else {
        vertex.data = data;
        vertex.cost = (cost == undefined) ? Vector.ZERO : cost instanceof Vector ? cost : new Vector(cost);
    }
};
Graph.prototype.updateVertex = Graph.prototype.addVertex;

Graph.prototype.hasVertex = function (data) {
    return this._vertices.contains(data);
};

Graph.prototype.getVertices = function () {
    var vertexSet = new HashSet();
    vertexSet.addAll(this._vertices.keys());
    return vertexSet;
};

Graph.prototype.getConnectedVerticesFor = function (vertex) {
    if (!this.hasVertex(vertex)) return null;
    else {
        var connectedVertices = this._vertices.get(vertex).getConnectedVertices();
        return connectedVertices.map( function (graphVertex) { return graphVertex.data; } );
    }
};

Graph.prototype.getNextVerticesFor = function (vertex) {
    if (!this.hasVertex(vertex)) return null;
    else {
        var nextVertices = this._vertices.get(vertex).getNextVertices();
        return nextVertices.map( function (graphVertex) { return graphVertex.data; } );
    }
};

Graph.prototype.forEachVertex = function (f) {
    this._vertices.forEach(function (data, graphVertex){
        f(data);
    });
};

Graph.prototype.forEachPair = function (f) {
    var visited = new HashSet();
    this._vertices.forEach(function (key, graphVertex){
        visited.forEach(function (otherVertex) {
            f(graphVertex.data, otherVertex);
        });
        visited.add(graphVertex.data);
    });
};

Graph.prototype.removeVertex = function (data) {
    var self = this;
    var graphVertex = this._vertices.get(data);
    if(!graphVertex) return;
    while(graphVertex.edges.size() > 0){
        this._removeEdge(graphVertex.edges.getOne());
    }
    this._vertices.remove(data);
};

// Deal with edges:
Graph.prototype.addEdge = function (source, target, cost, data) {
    var edge = this._getEdge(source, target);
    if (!edge){
        var sourceGraphVertex = this._vertices.get(source);
        var targetGraphVertex = this._vertices.get(target);
        if(sourceGraphVertex && targetGraphVertex && sourceGraphVertex != targetGraphVertex) {
            var edge = new Edge(sourceGraphVertex, targetGraphVertex, cost, data);
            this._edges.add(edge);
            sourceGraphVertex.edges.add(edge);
            sourceGraphVertex.fanout.add(edge);
            targetGraphVertex.edges.add(edge);
            targetGraphVertex.fanin.add(edge);
        }
    } else {
        edge.cost = (cost == undefined) ? Vector.ZERO : cost instanceof Vector ? cost : new Vector(cost);
        edge.data = data;
    }
};
Graph.prototype.updateEdge = Graph.prototype.addEdge;

Graph.prototype.hasEdge = function(source, target){
    return this._getEdge(source, target) != undefined;
};

Graph.prototype.getEdges = function () {
    return this._edges.map(function(edge) {
       return edge.toObject();
    });
};

Graph.prototype.getEdgesFor = function (vertex) {
    if (!this.hasVertex(vertex)) return null;
    else return this._vertices.get(vertex).edges.map(function(edge) {
       return edge.toObject();
    });
};

Graph.prototype.forEachEdge = function (f) {
    this._edges.forEach(function (edge){
        f(edge.toObject());
    });
};

Graph.prototype.removeEdge = function (source, target) {
    var edge = this._getEdge(source, target);
    if(edge) this._removeEdge(edge);
};

// Internal graph function:
Graph.prototype._getEdge = function (source, target) {
    var sourceGraphVertex = this._vertices.get(source);
    var targetGraphVertex = this._vertices.get(target);
    if(!sourceGraphVertex || !targetGraphVertex) return;
    return sourceGraphVertex.fanout.find(function(edge){
        return edge.target === targetGraphVertex;
    });
};

Graph.prototype._removeEdge = function (edge) {
    this._edges.remove(edge);
    edge.source.edges.remove(edge);
    edge.source.fanout.remove(edge);
    edge.target.edges.remove(edge);
    edge.target.fanin.remove(edge);
};

Graph.prototype._copyVerticesFrom = function (graph) {
    var self = this;
    graph._vertices.forEach(function(vertex, graphVertex){
        self.addVertex(graphVertex.data, graphVertex.cost);
    });
};

// Graph algorithms:
Graph.prototype.equals = function(graph) {
    var hasSameVertices = this._vertices.equals(graph._vertices, function(vertex1, vertex2){
        return vertex1.cost.equals(vertex2.cost);    // data is already compared as key in this map compare
    });
    if (!hasSameVertices) return false;
    return this._edges.equals(graph._edges, function(edge1, edge2){
        return  edge2.source == graph._vertices.get(edge1.source.data) &&
                edge2.target == graph._vertices.get(edge1.target.data) &&
                edge2.cost.equals(edge1.cost) &&
                edge2.data == edge2.data;
    });
};

Graph.prototype.copy = function () {
    var self = this;
    var graph = new Graph(this._vhash);
    this._vertices.forEach(function(k, v){
        graph.addVertex(v.data, v.cost);
    });
    this._edges.forEach(function(e){
        graph.addEdge(e.source.data, e.target.data, e.cost, e.data);
    });
    return graph;
};

Graph.prototype.connectedComponents = function () {
    var self = this;
    var visitedVertices = new HashSet();
    var visitedEdges = new HashSet();
    var components = new HashSet();

    this._vertices.forEach( function (d, vertex) {
        if (!visitedVertices.contains(vertex)) {
            var component = new Graph(self._vhash);
            components.add(component);
            component.addVertex(vertex.data, vertex.cost);
            var verticesToVisit = new HashSet();
            verticesToVisit.add(vertex);
            while(!verticesToVisit.isEmpty()) {
                var v1 = verticesToVisit.pop();
                visitedVertices.add(v1);
                v1.edges.forEach(function(edge){
                    if (!visitedEdges.contains(edge)) {
                        var v2 = edge.other(v1);
                        component.addVertex(v2.data, v2.cost);
                        component.addEdge(edge.source.data, edge.target.data, edge.cost, edge.data);
                        verticesToVisit.add(v2);
                        visitedEdges.add(edge);
                    }
                });
            }
        }
    });
    return components;
};

Graph.prototype.getConnectedVertexSets = function () {
    var self = this;
    var visitedVertices = new HashSet();
    var visitedEdges = new HashSet();
    var components = new HashSet();

    this._vertices.forEach( function (d, vertex) {
        if (!visitedVertices.contains(vertex)) {
            var component = new HashSet(self._vhash);
            components.add(component);
            component.add(vertex.data);
            var verticesToVisit = new HashSet();
            verticesToVisit.add(vertex);
            while(!verticesToVisit.isEmpty()) {
                var v1 = verticesToVisit.pop();
                visitedVertices.add(v1);
                v1.edges.forEach(function(edge){
                    if (!visitedEdges.contains(edge)) {
                        var v2 = edge.other(v1);
                        component.add(v2.data);
                        verticesToVisit.add(v2);
                        visitedEdges.add(edge);
                    }
                });
            }
        }
    });
    return components;
};

Graph.prototype.minimumSpanningTree = function () {
    var tree = new Graph(this._vhash);
    tree._copyVerticesFrom(this);

    //Initialize
    var toVisit = new HashSet();
    toVisit.addAll(this._vertices);
    var vertex = toVisit.pop();

    //Prim algorithm
    while (toVisit.size()>0) {
        var candidateEdges = vertex.edges.filter(function(edge) {
            return (toVisit.contains(edge.source) || toVisit.contains(edge.target)) && !tree.hasEdge(edge.source.data, edge.target.data);
        });
        if(!candidateEdges.size()) return null;
        var edge = candidateEdges.getMin(function(e1, e2) {
            return e1.cost.compare(e2.cost);
        });
        tree.addEdge(edge.source.data, edge.target.data, edge.cost, edge.data);
        vertex = edge.other(vertex);
        toVisit.remove(vertex);
    }
    return tree;
};

// TODO: following algorithm is NOT correct, also first find a good definition of what this needs to return!
// Graph.prototype.cycles = function () {
//     var self = this;
//     var cycles = new HashSet();
//     this.connectedComponents().forEach(function(subGraph){
//         var visitedEdges = new HashSet();
//         var visitedVertices = new HashSet();
//         var back = new Graph(self._vhash);
//         var tree = new Graph(self._vhash);
//         back._copyVerticesFrom(subGraph);
//         tree._copyVerticesFrom(subGraph);
//
//         function cycles_rec(vertex) {
//             vertex.edges.forEach(function(edge){
//                 if (!visitedEdges.contains(edge)) {
//                     visitedEdges.add(edge);
//                     var next = edge.other(vertex);
//                     if (!visitedVertices.contains(next)) {
//                         visitedVertices.add(next);
//                         tree.addEdge(edge.source.data, edge.target.data, edge.cost, edge.data);
//                         cycles_rec(next);
//                     } else {
//                         back.addEdge(edge.source.data, edge.target.data, edge.cost, edge.data);
//                     }
//                 }
//             });
//         }
//
//         var seed = subGraph._vertices.getOne();
//         visitedVertices.add(seed);
//         cycles_rec(seed);
//
//         back._edges.forEach(function(backEdge){
//             var cycle = [];
//             var start = subGraph._vertices.get(backEdge.source.data);
//             var end = subGraph._vertices.get(backEdge.target.data);
//
//             var current = start;
//             cycle.push(current.data);
//             while (current !== end) {
//                 current = current.fanin.getOne().other(current);
//                 cycle.push(current.data);
//             }
//
//             cycles.add(cycle);
//         });
//     });
//     return cycles;
// };

Graph.prototype.undirectedCycles = function() {
    // http://stackoverflow.com/questions/12367801/finding-all-cycles-in-undirected-graphs
    var cycles = new HashSet();

    this._edges.forEach(function (e) {
        var vertices = new HashSet();
        vertices.add(e.source);
        vertices.add(e.target);
        vertices.forEach(function (vertex) {
            findNewCycles([vertex]);
        });
    });

    function findNewCycles(path) {
        var start = path[0];
        var next = null;
        var sub = [];

        // visit each edge and each node of each edge
        start.edges.forEach(function (e) {
            next = e.other(start);
            if (path.indexOf(next) < 0) {
                // neighbor node not on path yet
                sub = [next];
                sub = sub.concat(path);
                // explore extended path
                findNewCycles(sub);
            } else if (path.length > 2 && next === path[path.length-1]) {
                // cycle found
                var cycle = new HashSet();
                cycle.addAll(path.map(function (v) { return v.data; }));
                if (isNew(cycle)) cycles.add(cycle);
            }
        });
    }

    function isNew(cycle) {
        return !cycles.some(function (otherCycle) {
            return otherCycle.equals(cycle);
        });
    }

    return cycles;
};

Graph.prototype.tarjanStronglyConnectedComponents = function() {
    // https://en.wikipedia.org/wiki/Tarjan%27s_strongly_connected_components_algorithm
    // DIRECTED ONLY !!!
    var i = 0;
    var S = []; // TODO: set??
    var components = new HashSet();

    var lowlinkMap = new HashMap();
    var indexMap = new HashMap();
    var stack = new HashSet();

    this._vertices.forEach(function (key, v) {
        if (indexMap.get(v) === undefined)
            strongconnect(v);
    });

    function strongconnect(v) {
        // Set the depth index for v to the smallest unused index
        indexMap.put(v, i);
        lowlinkMap.put(v, i);
        i = i + 1;
        S.push(v);
        stack.add(v);

        // Consider successors of v
        v.fanout.forEach(function (e) {
            var w = e.other(v);
            if (indexMap.get(w) === undefined) {
              // Successor w has not yet been visited; recurse on it
              strongconnect(w);
              lowlinkMap.put(v, Math.min(lowlinkMap.get(v), lowlinkMap.get(w)));
            } else if (stack.contains(e.target)) {
              // Successor w is in stack S and hence in the current SCC
              lowlinkMap.put(v, Math.min(lowlinkMap.get(v), indexMap.get(w)));
            }
        });

        // If v is a root node, pop the stack and generate an SCC
        if (lowlinkMap.get(v) === indexMap.get(v)) {
            // start a new strongly connected component
            var component = [];
            var w;
            do {
                w = S.pop();
                stack.remove(w);
                // add w to current strongly connected component
                component.push(w.data);
            } while (w != v);
            components.add(component);
        }
    }

    return components;
};

Graph.prototype.breadthFirstAllPaths = function(source, sink) {
    var vSource = this._vertices.get(source);
    var vSink = this._vertices.get(sink);

    var result = new HashSet();

    var pi = new Bins();

    var visited = new HashSet();
    function bfs(vertex){
        visited.add(vertex);
        if (vertex !== vSink) {
            vertex.getConnectedVertices().forEach(function(child){
                pi.put(child, vertex);
                if (!visited.contains(child)) bfs(child);
            });
        }
    }

    bfs(vSource);

    function backtrack (path) {
        var prevs = pi.get(path[0]);
        prevs.forEach(function (prev) {
            if(path.indexOf(prev) < 0) {
                var sub = [prev].concat(path);
                if(prev === vSource)
                    result.add(sub.map(function (v) { return v.data; }));
                else
                    backtrack(sub);
            }
        });
    }

    backtrack([vSink]);

    return result;
};

Graph.prototype.breadthFirstPath = function(source, sink) {
    var vSource = this._vertices.get(source);
    var vSink = this._vertices.get(sink);

    var path = [];
    var visited = new HashSet();
    function bfs(vertex){
        visited.add(vertex);
        var found = false;
        if (vertex === vSink) found = true;
        else {
            vertex.getConnectedVertices().forEach(function(child){
                if (!visited.contains(child)) found |= bfs(child);
            });
        }
        if (found) path.unshift(vertex.data); // backtrack
        return found;
    }
    if (bfs(vSource)) return path;
    else return null;
};

Graph.prototype.breadthFirstPathEdges = function(source, sink) {
    var vSource = this._vertices.get(source);
    var vSink = this._vertices.get(sink);

    var path = [];
    var visited = new HashSet();
    function bfs(vertex){
        visited.add(vertex);
        var found = false;
        if (vertex === vSink) found = true;
        else {
            vertex.edges.forEach(function(edge){
                var child = edge.other(vertex);
                if (!visited.contains(child)) {
                    var found_one = bfs(child);
                    if (found_one) path.unshift(edge); // backtrack
                    found |= found_one;
                }
            });
        }
        return found;
    }
    if (bfs(vSource)) return path.map(function(e) { return e.toObject(); });
    else return null;
};

Graph.prototype.aStarShortestPath = function(source, sinks, heuristic){
    var self = this;

    var vSource = this._vertices.get(source);
    var vSinks = null;
    if (sinks instanceof HashSet){
        vSinks = sinks.map(function(vertex) { return self._vertices.get(vertex); });
    } else {
        vSinks = new HashSet();
        vSinks.add(self._vertices.get(sinks));
    }

    if(this._edges.size() === 0) {
        if(!vSinks.contains(vSource)) return null;
        else return [source];
    }

    var prevMap = new HashMap();
    var costMap = new HashMap();
    var queue = new Heap(false, Vector.compare);

    costMap.put(vSource, Vector.ZERO);
    queue.push(vSource, costMap.get(vSource));
    while(queue.size() > 0) {
        var prevVertex = queue.pop();
        if (vSinks.contains(prevVertex)) {
            var path = [prevVertex.data];
            while(prevMap.get(prevVertex)) {
                prevVertex = prevMap.get(prevVertex);
                path.unshift(prevVertex.data);
            }
            return path;
        } else {
            prevVertex.edges.forEach(function(edge){
                var nextVertex = edge.other(prevVertex);
                var alt = costMap.get(prevVertex).add(edge.cost).add(nextVertex.cost);
                var cost = costMap.get(nextVertex);
                cost = cost !== undefined ? cost : Vector.INFINITY;
                if(alt.lessThan(cost)){
                    costMap.put(nextVertex, alt);
                    var fScore = heuristic ? alt.add(new Vector(heuristic(nextVertex.data, sinks))) : alt;
                    queue.push(nextVertex, fScore);
                    prevMap.put(nextVertex, prevVertex);
                }
            });
        }
    }
    return null;
};

// https://en.wikipedia.org/wiki/Dijkstra%27s_algorithm
Graph.prototype.dijkstraShortestPath = function(source, sinks){
    return this.aStarShortestPath(source, sinks);
};

// Algorithm based on 'http://www.cs.princeton.edu/courses/archive/spr09/cos423/Lectures/bi-gabow.pdf'
Graph.prototype.biconnectedComponents = function(){
    var graph = this;
    var vertex;
    var S = [];
    var B = [];
    var c = graph._vertices.size();
    var n = c+1;
    var color = 0;

    var biconnectedSubGraphs = [];     // list of biconnected subgraphs
    var subGraph;                      // list of components (vertices & edges) in a biconnected subgraph

    graph._vertices.forEach(function(d, vertex){
        vertex.color = [];
        vertex.bccIndex = 0;
        vertex.isArticulationPoint = false;
        vertex.visitedBy = null;
    });

    graph._vertices.forEach(function(d, v){
        if (v.bccIndex === 0){
            if (v.edges.size() == 0){  // if vertex is isolated add new subgraph with single vertex
                c++;
                color = c-n;
                subGraph = new Graph(graph._vhash);
                biconnectedSubGraphs.push(subGraph);
                v.bccIndex=c;
                v.color.push(color);   // color vertex and add to subgraph
                subGraph.addVertex(v.data, v.cost);
            }
            else DFS(v);
        }
    });

    function DFS(v){
        v.bccIndex = S.push(v);
        if (v.bccIndex > 1) B.push(v.bccIndex);
        v.getConnectedVertices().forEach(function(w){
            if (w.bccIndex === 0) {
                B.push(v.bccIndex);
                w.prevVisited = v;
                DFS(w);
            }
            else {
                while (v.bccIndex > 1 && w.bccIndex < B[B.length-2]){
                    B.pop();B.pop();
                }
            }
        });
        if (v.bccIndex === 1) {
            vertex = S.pop();
            vertex.bccIndex = c;
            vertex.color.push(color);
            subGraph.addVertex(vertex.data, vertex.cost);
        }
        else if (v.bccIndex === B[B.length-1]){
            B.pop();B.pop();

            // start new biconnected subgraph
            c++;
            color = c-n;
            subGraph = new Graph(graph._vhash);
            biconnectedSubGraphs.push(subGraph);

            while (v.bccIndex <= S.length){
                vertex = S.pop();
                vertex.bccIndex = c;

                // color vertex and add to subgraph(s)
                vertex.color.push(color);
                subGraph.addVertex(vertex.data, vertex.cost);
                if (vertex.visitedBy !== null) {
                    vertex.isArticulationPoint=true;
                    vertex.color.push(vertex.visitedBy);
                    biconnectedSubGraphs[vertex.visitedBy].addVertex(vertex.data, vertex.cost);
                }
            }

            var possibleAR = vertex.prevVisited;
            if (possibleAR.visitedBy !== null) {
                possibleAR.isArticulationPoint=true;
                possibleAR.color.push(possibleAR.visitedBy);
                biconnectedSubGraphs[possibleAR.visitedBy].addVertex(possibleAR.data, possibleAR.cost);
            }
            possibleAR.visitedBy = color;
        }
    }

    // color edges
    graph._edges.forEach(function(edge){
        var color = Math.min(edge.source.bccIndex, edge.target.bccIndex) - n;
        edge.color = color;
        biconnectedSubGraphs[color].addEdge(edge.source.data, edge.target.data, edge.cost, edge.data);
    });

    var result = new HashSet();
    result.addAll(biconnectedSubGraphs);
    return result;
};

// For debugging purposes only!
// WARNING: order of vertices and edges in graph string is not guaranteed => NEVER use this toString function as a hash function nor use the result in subsequent code!
Graph.prototype.toString = function() {
    console.warn("Only use graph toString function for debugging purposes!");
    var result = "";

    result += "Vertices: \n";
    this.forEachVertex(function(vertex) {
        result += vertex.toString() + "\n";
    });

    result += "\n";
    result += "Edges: \n";
    this._edges.forEach(function(edge) {
        result += edge.toString() + "\n";
    });

    return result;
};

Vertex.prototype.toString = function() {
    return this.data.toString();
};

Edge.prototype.toString = function() {
    var result = "[";
    result += this.source.toString();
    result += ", ";
    result += this.target.toString();
    result += "]";
    return result;
};

module.exports = Graph;
